import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
// Circular but safe: the sync hook imports `useWorkflowChrome` from this
// file. ESM hoists the function binding so the read at render time always
// resolves; nothing in this file is evaluated against the hook at module
// load.

import { useWorkflowChromeStatusSync } from '../../hooks/useWorkflowChromeStatusSync';

export interface WorkflowToolbarAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Optional Mantine button size override. Defaults to 'sm'. */
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

export interface WorkflowToolbarSlots {
  saveAction?: WorkflowToolbarAction;
  exportAction?: WorkflowToolbarAction;
  primaryAction?: WorkflowToolbarAction;
}

export interface WorkflowChromeLabels {
  projectLabel?: string;
  periodLabel?: string;
  companyLabel?: string;
  showCompany?: boolean;
  showComments?: boolean;
  onCommentsClick?: () => void;
  /** Workflow id used by the stepper to fetch real stage data. */
  workflowId?: string;
  /** Period id used by the stepper to fetch real stage data. */
  periodId?: string;
  /** Which step represents the page the user is currently viewing.
   *  Drives the stepper's blue title highlight + back/next navigation. */
  activeStageKey?: string;
  /** Optional: forces the stepper's Next button into a disabled state — set
   *  by the active page when its own "Next" CTA is blocked. */
  nextStepDisabled?: boolean;
  /** Optional: overrides the Next button's onClick. If set, the stepper calls
   *  this instead of plain navigation — useful when the page's "Next" CTA has
   *  side effects (e.g. opening a drawer when prerequisites aren't met). */
  nextStepHandler?: () => void;
  /** Overrides the Back button's onClick. Also enables the button when there
   *  is no previous stepper stage. */
  backStepHandler?: () => void;
  /** Optional: overrides the stepper Next-button tooltip label. Use when the
   *  page's `nextStepHandler` will jump past the immediate next stage —
   *  e.g. the Funds page skips Jurisdictions and lands on Extraction when a
   *  jurisdiction already exists, so the tooltip should read "Extraction"
   *  rather than the default "Jurisdictions" / "Fund Info". */
  nextStepLabel?: string;
  /** Optional: arbitrary JSX rendered in the toolbar's left slot. Pages use
   *  this to inject page-specific controls (file/sheet selectors, tabs, etc.)
   *  alongside the global filters + slot-driven action buttons. */
  toolbarLeftContent?: React.ReactNode;
  /** Optional: arbitrary JSX rendered in the toolbar's right slot, BEFORE the
   *  built-in saveAction / exportAction / primaryAction buttons. Use this for
   *  controls that don't fit the button-only WorkflowToolbarAction shape —
   *  dropdowns, popovers, kebab menus, etc. */
  toolbarRightContent?: React.ReactNode;
  /** Whether the toolbar's built-in Multi-AIF View toggle should render. */
  showMultiAifToggle?: boolean;
  /** Optional: where the design switch should go from this page, when the generic route table
   *  cannot work it out — and what to ask first. The report page publishes both: its way back to
   *  the classic page needs the regulator, which only it knows, and it is the one page with
   *  unsaved edits to lose on the way. `confirm` returning false cancels the switch. */
  designSwitch?: { to: string; confirm?: () => boolean };
}

export interface WorkflowChromeValue extends WorkflowChromeLabels {
  toolbarSlots: WorkflowToolbarSlots | null;
  setToolbarSlots: (slots: WorkflowToolbarSlots | null) => void;
  multiAifView: boolean;
  setMultiAifView: (value: boolean) => void;
  setLabels: (labels: WorkflowChromeLabels) => void;
  clearLabels: () => void;
}

const WorkflowChromeContext = createContext<WorkflowChromeValue | null>(null);

// Stable actions-only context. Pages that only need to publish to chrome
// (Header, FormPF FundInfo, Setup, etc.) subscribe here and don't re-render
// on state changes — breaks the publish → state-update → re-render →
// republish loop that the full context would cause.
interface WorkflowChromeActions {
  setLabels: (labels: WorkflowChromeLabels) => void;
  clearLabels: () => void;
  setToolbarSlots: (slots: WorkflowToolbarSlots | null) => void;
  setMultiAifView: (value: boolean) => void;
}
const WorkflowChromeActionsContext = createContext<WorkflowChromeActions | null>(null);

const EMPTY_LABELS: WorkflowChromeLabels = {};

export const WorkflowChromeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toolbarSlots, setToolbarSlots] = useState<WorkflowToolbarSlots | null>(null);
  const [multiAifView, setMultiAifView] = useState(false);
  const [labels, setLabelsState] = useState<WorkflowChromeLabels>(EMPTY_LABELS);

  const setToolbarSlotsStable = useCallback((slots: WorkflowToolbarSlots | null) => {
    setToolbarSlots(slots);
  }, []);

  const setLabels = useCallback((next: WorkflowChromeLabels) => {
    setLabelsState(prev => ({ ...prev, ...next }));
  }, []);

  const clearLabels = useCallback(() => {
    setLabelsState(EMPTY_LABELS);
  }, []);

  const value = useMemo<WorkflowChromeValue>(
    () => ({
      ...labels,
      toolbarSlots,
      setToolbarSlots: setToolbarSlotsStable,
      multiAifView,
      setMultiAifView,
      setLabels,
      clearLabels
    }),
    [labels, toolbarSlots, setToolbarSlotsStable, multiAifView, setLabels, clearLabels]
  );

  const actions = useMemo<WorkflowChromeActions>(
    () => ({
      setLabels,
      clearLabels,
      setToolbarSlots: setToolbarSlotsStable,
      setMultiAifView
    }),
    [setLabels, clearLabels, setToolbarSlotsStable, setMultiAifView]
  );

  return (
    <WorkflowChromeContext.Provider value={value}>
      <WorkflowChromeActionsContext.Provider value={actions}>
        <WorkflowChromeStatusSyncer />
        {children}
      </WorkflowChromeActionsContext.Provider>
    </WorkflowChromeContext.Provider>
  );
};

export const useWorkflowChrome = (): WorkflowChromeValue => {
  const ctx = useContext(WorkflowChromeContext);
  if (!ctx) {
    throw new Error('useWorkflowChrome must be used inside <WorkflowChromeProvider>');
  }
  return ctx;
};

// Stable actions-only hook — use when a page only needs to publish to chrome.
export const useWorkflowChromeActions = (): WorkflowChromeActions => {
  const ctx = useContext(WorkflowChromeActionsContext);
  if (!ctx) {
    throw new Error('useWorkflowChromeActions must be used inside <WorkflowChromeProvider>');
  }
  return ctx;
};

// Internal helper component: invokes the chrome-wide SignalR sync hook once
// per provider so every stepper-bearing page benefits from automatic period
// invalidation when extraction / transformation / report-generation events
// arrive.
const WorkflowChromeStatusSyncer: React.FC = () => {
  useWorkflowChromeStatusSync();
  return null;
};
